import { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";

import { AuthnFormDialog } from "@/web/auth/AuthnFormDialog";
import { useUser } from "@/web/auth/useUser";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { PendingInvitesPrompt } from "@/web/routes/_protected/-PendingInvitesPrompt";
import { usePostHog } from "@posthog/react";

export const Route = createFileRoute("/_protected")({
  component: MainAuthnWrapper,
});

/**
 * Address to pre-fill into the sign-in form, read from `?email=` on any
 * protected route. Invited onboarding links carry it so the owner does not
 * retype an address we already hold on their invited-brand draft.
 *
 * Display convenience ONLY — it is user-editable and proves nothing. Auth still
 * runs in full, and the server matches the resulting Clerk email against the
 * draft (`assertDraftUsable`) before any claim.
 */
function prefillEmailFromSearch(search: Record<string, unknown>): string | undefined {
  const raw = search.email;
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim().toLowerCase();
  // Cheap sanity check: only shapes that could plausibly be an address reach
  // the field, so a junk param leaves an empty input rather than filling it
  // with garbage the user has to clear.
  return trimmed.includes("@") ? trimmed : undefined;
}

function MainAuthnWrapper() {
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const user = useUser();
  const posthog = usePostHog();
  // `useLocation` rather than a route-specific `Route.useSearch()`: this shell
  // wraps every protected route, so it can't bind to one route's search shape.
  const prefillEmail = useLocation({
    select: (l) => prefillEmailFromSearch(l.search as Record<string, unknown>),
  });
  const clerkUserId = user?.clerkUserId;
  const email = user?.email;
  const isWithcoEmployee = user?.isWithcoEmployee;
  const brandIdsKey = user?.brandIds.map((id) => id.toString()).join("|") ?? "";
  const investorIdsKey = user?.investorIds.map((id) => id.toString()).join("|") ?? "";

  useEffect(() => {
    if (!clerkUserId) {
      Sentry.setUser(null);
      return;
    }

    const brandIds = brandIdsKey === "" ? [] : brandIdsKey.split("|");
    const investorIds = investorIdsKey === "" ? [] : investorIdsKey.split("|");

    posthog.identify(clerkUserId, {
      email,
      brandIds,
      investorIds,
    });

    Sentry.setUser({
      id: clerkUserId,
      email,
      brandIds,
      investorIds,
    });
    if (isWithcoEmployee) {
      Sentry.setTag("role", "withco");
    } else {
      Sentry.setTag("role", "user");
    }
  }, [clerkUserId, email, isWithcoEmployee, brandIdsKey, investorIdsKey, posthog]);

  if (!clerkLoaded) {
    return <FullPageLoader />;
  }

  if (user) {
    return (
      <>
        <PendingInvitesPrompt />
        <Outlet />
      </>
    );
  }

  // Clerk session is active but Spacetime `currentUser` has not arrived yet (common right after login).
  if (isSignedIn) {
    return <FullPageLoader />;
  }

  return (
    <AuthnFormDialog
      open
      onOpenChange={() => {}}
      showCloseButton={false}
      defaultEmail={prefillEmail}
    />
  );
}
